// Smart-Send (מסך השליחה החכמה) models.
// Mirrors: C# DAL.Models.DataSources.SmartSendModels (eSendChannel : byte) and the
// §7½ Gate-0 contract resultsets (DataSources-DB-MasterPlan, anchor sp-contract-smartsend).
// PascalCase fields = server JSON keys (PulseemResponse.Data shapes) — no client renaming.

// Mirror of DataSources_FillCampaignGroup @prm_Channel (tinyint), and — since 78a — of the
// exclusive arc in dbo.CampaignsToDataSources:
//   1 → dbo.Campaigns.CampaignID · 2 → dbo.SMSCampaign.SMSCampaignID
//   3 → dbo.WhatsAppCampaign.WACampaignID
// 🔴 These numeric values are on the wire AND in the database. They never change.
export enum eSendChannel { EMAIL = 1, SMS = 2, WHATSAPP = 3 }

export interface ChannelDescriptor {
    channel: eSendChannel;
    labelKey: string;
    icon: 'email' | 'sms' | 'whatsapp';
    // 🔴 `enabled` IS A PER-CHANNEL CAPABILITY FACT, NOT A RANK. It answers one question:
    // "does this channel have a working end-to-end route today?" — it never means "this is
    // the main channel". Email and WhatsApp both answer yes; SMS does not yet, because it
    // has no server route and no container flow. The day it does, this one flag flips and
    // nothing else in the client changes.
    enabled: boolean;
    comingSoon: boolean;
    identityFlag: 'HasEmailIdentity' | 'HasCellIdentity';       // which GetMany flag gates a source
    clientIdField: 'EmailClientID' | 'CellClientID';            // which ClientID FillCampaignGroup resolves
    resolvedCountField: 'ResolvedRowsEmail' | 'ResolvedRowsCell';
    duplicateFlag: 'IsEmailDuplicate' | 'IsCellDuplicate';
}

// The single source of truth for channel-dependent behavior. Every channel-aware
// spot reads field NAMES from here — never hardcodes EmailClientID etc. Adding a
// future channel = flipping enabled:false→true on one row (plus server wiring).
export const CHANNELS: ChannelDescriptor[] = [
    { channel: eSendChannel.EMAIL, labelKey: 'DataSources.send.channel.email', icon: 'email',
      enabled: true,  comingSoon: false, identityFlag: 'HasEmailIdentity', clientIdField: 'EmailClientID',
      resolvedCountField: 'ResolvedRowsEmail', duplicateFlag: 'IsEmailDuplicate' },
    { channel: eSendChannel.SMS, labelKey: 'DataSources.send.channel.sms', icon: 'sms',
      enabled: false, comingSoon: true,  identityFlag: 'HasCellIdentity',  clientIdField: 'CellClientID',
      resolvedCountField: 'ResolvedRowsCell', duplicateFlag: 'IsCellDuplicate' },
    // 🔴 WHATSAPP IS WIRED. This single flag was the narrowest chokepoint in the whole client:
    // `setChannel` (smartSendSlice) refuses any descriptor whose `enabled` is false, and so does
    // ChannelSelector's `select`, so with it false the client was PHYSICALLY UNABLE to represent
    // a WhatsApp mapping — `selectedChannel` could not hold 3 at all. 78a gave WhatsApp its own
    // arc row, its own token map and its own container, so the fact this flag reports is now true.
    { channel: eSendChannel.WHATSAPP, labelKey: 'DataSources.send.channel.whatsapp', icon: 'whatsapp',
      enabled: true,  comingSoon: false, identityFlag: 'HasCellIdentity',  clientIdField: 'CellClientID',
      resolvedCountField: 'ResolvedRowsCell', duplicateFlag: 'IsCellDuplicate' },
];

// 🔴 FAILS CLOSED. It used to end in `?? CHANNELS[0]`, which silently turned any channel the
// client did not recognise into EMAIL — the exact "absence ⇒ e-mail" 77 §7 forbids, sitting
// under SourcePicker, setChannel and every descriptor lookup. Absence of a descriptor is
// UNKNOWN, and the caller has to say what it does about that.
export const findChannelDescriptor = (channel: eSendChannel): ChannelDescriptor | undefined =>
    CHANNELS.find(c => c.channel === channel);

// Throwing variant for the call sites that genuinely cannot continue without one. A loud
// failure in development beats a send that silently went out on the wrong channel.
export const getChannelDescriptor = (channel: eSendChannel): ChannelDescriptor => {
    const d = CHANNELS.find(c => c.channel === channel);
    if (!d) throw new Error(`Unknown send channel: ${channel}`);
    return d;
};

// ── GetMapping (§10 + Gate-0 clone-detection fields) ─────────────────────────

export interface SmartSendTokenInfo {
    Token: string;               // exact, case-sensitive, without ##
    IsSystemField: boolean;      // ∈ the sender's closed set (30 + Verify)
    IsGraphToken: boolean;       // appears inside a pulseemmonitorgraph URL
    MappedColumnID: number | null;
}

export interface SmartSendColumn {
    ColumnID: number;
    Ordinal: number;
    SourceHeader: string;
    DisplayName: string;
    ColumnKey: string;
    DataType: number;            // 1=Text,2=Number,3=Date,4=Email,5=Phone
    FormatHint: number;          // 0=None,1=Currency,2=Percent
    SemanticRole: number;        // 0=None,1=RecipientEmail,2=RecipientCellphone
    IsSearchable: boolean;
}

export interface SmartSendDataSourceInfo {
    DataSourceID: number;
    Name: string;
    LockedVersionID: number;
    ActiveVersionID: number | null;
}

export interface GetMappingResult {
    CampaignID: number;
    IsMapped: boolean;
    IsStale: boolean;
    Mismatch: boolean;
    DataSource: SmartSendDataSourceInfo | null;
    SupervisorColumnID: number | null;
    GapColumnID: number | null;
    SortColumnID: number | null;
    SyntheticGroupID: number | null;
    ForeignSyntheticGroupID: number | null;    // clone detection (PO decision #6)
    ForeignSyntheticGroupName: string | null;
    Tokens: SmartSendTokenInfo[];
    Columns: SmartSendColumn[];
    // 12-fix-wave 7.1 — the send's two ids, read from ITS row (CampaignsToDataSources_Get RS1, 4.7).
    // `RowWACampaignID` is set only while that unit is present (not LogicalDeleted, contract §1.8).
    // Optional: an API that predates 5.10 does not send them.
    RowCampaignID?: number | null;
    RowWACampaignID?: number | null;
}

// ── GetTokensBulk (campaign-picker field counts) ─────────────────────────────
// POST api/DataSourcesSender/GetTokensBulk → Data: BulkTokensResult.
// Mirrors DAL.Models.DataSources.{CampaignTokensItem,BulkTokensResult}. Reuses
// SmartSendTokenInfo so the number on a picker card is the SAME number the mapping screen
// shows — both come from the server's single ExtractTokens. MappedColumnID is always null
// here (this call carries no mapping context).
// A requested CampaignID may be ABSENT from Items when the caller does not own it; the slice
// marks those ids failed rather than rendering a misleading 0.

export interface CampaignTokensItem {
    CampaignID: number;
    Tokens: SmartSendTokenInfo[];
}

export interface BulkTokensResult {
    Items: CampaignTokensItem[];
}

// ── SetMapping ───────────────────────────────────────────────────────────────

export interface TokenMapEntry {
    Token: string;
    DataSourceColumnID: number;
    GroupNo: number;
    GroupTitle: string | null;
    DisplayOrder: number;
}

// Wire note: the token-map array is named "Mappings" — the §10 endpoint JSON is
// the binding wire spec (the §12.3 sketch's "TokenMap" name was superseded;
// documented M4 decision). Channel travels in every request from day one (§3.3).
export interface SaveMappingRequest {
    CampaignID: number;
    Channel: eSendChannel;       // v1 always sends 1
    DataSourceID: number;
    DataSourceVersionID: number | null;   // null = lock the active version
    SupervisorColumnID: number | null;
    GapColumnID: number | null;
    SortColumnID: number | null;
    Mappings: TokenMapEntry[];
    // 12-fix-wave 7.1 / 7.4(d) — sent (true) only after the operator confirmed the reset dialog.
    ConfirmReset?: boolean;
}

// ── FillAndSummarize (§7½ exact aliases) ─────────────────────────────────────

export interface FillSummary {
    TotalRows: number;
    FinalClients: number;
    SkippedNoIdentity: number;
    SkippedDuplicates: number;
    SkippedRemovedOrMissing: number;
}

// ── Smart-Send management list (Session-B frozen contract) ───────────────────
// One row of the DataSources "Smart Send" management tab. PascalCase = server JSON
// keys (DataSources_GetSmartSendList resultset, mirrored 1:1 by the C# SmartSendListItem
// in DataSourceModels.cs). IsOutdated drives the amber "newer version available" chip;
// Latest* are null when the mapped version already IS the latest. CampaignStatus is an
// eCampaignStatus value (Models/Enums/Campaign) — the tab's status chip maps it exactly
// as CampaignPicker's STATUS_KEY does.
// 12-fix-wave 7.1 — ONE ROW PER SEND (contract §1). No `Channel`: which ids are present says what the
// send is. `CampaignID` is the e-mail campaign only; `WACampaignID` is the WhatsApp unit only.
export interface SmartSendListItem {
    CampaignID: number | null;
    WACampaignID: number | null;
    DataSourceID: number;
    DataSourceName: string;
    CampaignName: string | null;
    CampaignStatus: number | null;             // eCampaignStatus
    WACampaignName: string | null;
    WACampaignStatus: number | null;    // null ⇔ no present unit (§1.8)
    MappedVersionID: number;
    MappedVersionNumber: number;
    LatestVersionNumber: number | null;
    LatestVersionID: number | null;
    IsOutdated: boolean;
    SyntheticGroupID: number | null;
    LastUpdated: string | null;
}

// Data payload of GET DataSourcesSender/GetList (PulseemResponse.Data).
export interface SmartSendListResult {
    items: SmartSendListItem[];
    total: number;
    page: number;
    pageSize: number;
}

// 12-fix-wave 7.1 / 7.7 — one line of the source-delete refusal (409 LOCKED_BY_CAMPAIGNS,
// `Data.campaigns`): a send that still uses the source, with each present channel's name and status.
export interface BlockingCampaign {
    CampaignID: number | null;
    CampaignName: string | null;
    Status: number | null;
    WACampaignID: number | null;
    WACampaignName: string | null;
    WACampaignStatus: number | null;
}
